import {isFeatureEnabled} from 'feature-flags';
import {
  Card,
  Separator,
  Form,
  FormField,
  FormButton,
  Button,
  closeDialog,
  FormSubmitHandler,
  openDialog,
  showNotification,
  Tooltip,
} from 'platform/components';
import {GridItem, Grid, Hide, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState, useCallback} from 'react';
import {Link} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {useCreatePromotionalPhotoMutation, useEditPromotionalPhotoMutation} from '@dms/api/core';
import {FinancialProductType, PromotionalPhotoType} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getApiValidationErrors, handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {MAX_PROMO_PHOTO_SIZE_IN_BYTES} from '../../constants/MAX_PROMO_PHOTO_SIZE_IN_BYTES';
import {MAX_PROMO_PHOTO_SIZE_MB} from '../../constants/MAX_PROMO_PHOTO_SIZE_MB';
import {getFinancialProductTypeLabel} from '../../utils/getFinancialProductTypeLabel';
import {notifyUnsupportedImageFormat} from '../../utils/notifyUnsupportedImageFormat';
import {CompositionGraphicSection} from '../CompositionEditor/components/CompositionGraphicSection';
import {hasPhotoLayer} from '../CompositionEditor/utils/hasPhotoLayer';
import {Dialogs} from './components/Dialogs';
import {PromoPhotoVehicleSelectionSection} from './components/PromoPhotoVehicleSelectionSection';
import {UnsuportedPlatformsNotification} from './components/UnsuportedPlatformsNotification';
import {VehicleSelectionImpactPreview} from './components/VehicleSelectionImpactPreview/VehicleSelectionImpactPreview';
import {MIN_SOURCE_PHOTO_INDEX} from './constants/MIN_SOURCE_PHOTO_INDEX';
import {getPromoPhotosFormSchema} from './schemas/getPromoPhotosFormSchema';
import {DialogTypes} from './types/DialogTypes';
import {PromoPhotosFormType} from './types/PromoPhotosFormType';
import {PromoPhotosType} from './types/PromoPhotosType';
import {dataChanged} from './utils/dataChanged';
import {getDialogTypeByValues} from './utils/getDialogTypeByValues';
import {getPromoPhotoDefaultValues} from './utils/getPromoPhotoDefaultValues';
import {getPromoPhotoRequestData} from './utils/getPromoPhotoRequestData';
import {getPromoPhotoVehicleSelection} from './utils/getPromoPhotoVehicleSelection';
import {isPromoPhotoFileNotFoundError} from './utils/isPromoPhotoFileNotFoundError';
import {isTransparentBackground} from './utils/isTransparentBackground';

export interface PromoPhotosFormProps {
  editData?: PromoPhotosType;
}

const DEFAULT_SOURCE_PHOTO_INDEX = 1;
const TRANSPARENT_BACKGROUND_DIALOG_ID = 'promo-photo-transparent-background';

const PHOTO_TYPE_OPTIONS: {value: PromotionalPhotoType; label: string}[] = [
  {value: 'REGULAR', label: i18n.t('entity.promoPhotos.labels.typeRegular')},
  {value: 'FINANCING', label: i18n.t('entity.promoPhotos.labels.typeFinancing')},
];

const FINANCIAL_PRODUCT_OPTIONS: {value: FinancialProductType; label: string}[] = (
  ['STANDARD', 'BALLOON', 'LEASING'] as const
).map((type) => ({value: type, label: getFinancialProductTypeLabel(type)}));

// What `autoAssign` does depends on the type: a REGULAR photo is attached to matching sale
// vehicles by the BE (Magic Publishing adds REGULAR photos regardless of the toggle), a FINANCING
// configuration is picked up by Magic Publishing only when the toggle is on.
const AUTO_ASSIGN_TEXTS: Record<
  PromotionalPhotoType,
  {label: string; helperText: string; tooltip: string}
> = {
  REGULAR: {
    label: i18n.t('entity.promoPhotos.labels.autoAssignRegular'),
    helperText: i18n.t('entity.promoPhotos.labels.autoAssignRegularHelper'),
    tooltip: i18n.t('entity.promoPhotos.labels.autoAssignRegularTooltip'),
  },
  FINANCING: {
    label: i18n.t('entity.promoPhotos.labels.autoAssignFinancing'),
    helperText: i18n.t('entity.promoPhotos.labels.autoAssignFinancingHelper'),
    tooltip: i18n.t('entity.promoPhotos.labels.autoAssignFinancingTooltip'),
  },
};

export function PromoPhotosForm({editData}: PromoPhotosFormProps) {
  const [uploadFile] = useUploadFileMutation();
  const navigate = useNavigate();
  const [createPromotionalPhoto, {isLoading: createLoading}] = useCreatePromotionalPhotoMutation();
  const [editPromotionalPhoto, {isLoading: editLoading}] = useEditPromotionalPhotoMutation();
  const [dialogType, setDialogType] = useState<{type: DialogTypes; id: string} | null>(null);

  const isFinancingEnabled = isFeatureEnabled(featureFlags.FINANCING_PROMO_PHOTOS);
  const isVehicleSelectionEnabled = isFeatureEnabled(featureFlags.PROMO_PHOTO_VEHICLE_SELECTION);
  const isEditing = isNotNil(editData);

  const savePromoPhoto = useCallback<FormSubmitHandler<PromoPhotosFormType>>(
    async (data, setErrors) => {
      const requestBody = getPromoPhotoRequestData(data);
      // The uploaded file can disappear between upload and save; that is not a field error, so it
      // gets its own toast and asks for a re-upload instead of the raw BE message.
      const handleSaveError = (error: ApiError) => {
        if (isPromoPhotoFileNotFoundError(error)) {
          showNotification.error(i18n.t('entity.promoPhotos.notifications.fileNotFound'));
          return;
        }
        handleApiError(error, {
          callback: ({error}) => setErrors(getApiValidationErrors(error)),
        });
      };
      if (editData) {
        if (dataChanged(editData, data)) {
          navigate(settingsRoutes.advertisingPromoPhotos);
          return;
        }

        await editPromotionalPhoto({
          promotionalPhotoId: editData.id,
          editPromotionalPhotoRequestBody: requestBody,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('entity.promoPhotos.notifications.edited'));
            const dialogType = getDialogTypeByValues(editData, data);
            if (dialogType) {
              setDialogType({type: dialogType, id: editData.id});
            } else {
              navigate(settingsRoutes.advertisingPromoPhotos);
            }
          })
          .catch(handleSaveError);
      } else {
        await createPromotionalPhoto({createPromotionalPhotoRequestBody: requestBody})
          .unwrap()
          .then((data) => {
            showNotification.success(i18n.t('entity.promoPhotos.notifications.create'));

            setDialogType({type: DialogTypes.ADDED, id: data.id});
          })
          .catch(handleSaveError);
      }
    },
    [createPromotionalPhoto, editPromotionalPhoto, setDialogType, navigate, editData]
  );

  // A transparent financing card shows whatever is behind it in the gallery and on the portals,
  // so saving one needs an explicit confirmation.
  const handleSubmit: FormSubmitHandler<PromoPhotosFormType> = async (data, setErrors, reset) => {
    if (
      data.type !== 'FINANCING' ||
      !isTransparentBackground(data.composition?.canvas.background)
    ) {
      await savePromoPhoto(data, setErrors, reset);
      return;
    }
    openDialog(<Text>{i18n.t('entity.promoPhotos.dialogs.transparentBackground')}</Text>, {
      id: TRANSPARENT_BACKGROUND_DIALOG_ID,
      size: 'small',
      title: i18n.t('entity.watermark.composition.labels.transparentBackground'),
      buttons: [
        {
          variant: 'secondary',
          title: i18n.t('general.actions.cancel'),
          onClick: () => closeDialog(TRANSPARENT_BACKGROUND_DIALOG_ID),
          'data-testid': 'settings-advertising-promoPhotos-form-transparentBackground-cancel',
        },
        {
          variant: 'primary',
          title: i18n.t('entity.promoPhotos.actions.saveAnyway'),
          onClick: () => {
            closeDialog(TRANSPARENT_BACKGROUND_DIALOG_ID);
            void savePromoPhoto(data, setErrors, reset);
          },
          'data-testid': 'settings-advertising-promoPhotos-form-transparentBackground-confirm',
        },
      ],
      'data-testid': 'settings-advertising-promoPhotos-form-transparentBackground',
    });
  };

  const handleUpload = async (file: File) => {
    if (file.size > MAX_PROMO_PHOTO_SIZE_IN_BYTES) {
      const errorMessage = i18n.t('entity.photo.labels.maxUploadFileSize', {
        max: MAX_PROMO_PHOTO_SIZE_MB,
      });

      showNotification.error(errorMessage);
      return Promise.reject();
    }
    const data = await uploadFile({file}).unwrap();

    return {id: data.fileId, url: data.fileUri};
  };

  return (
    <>
      <Form<PromoPhotosFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={getPromoPhotosFormSchema(isVehicleSelectionEnabled)}
        defaultValues={getPromoPhotoDefaultValues(editData)}
      >
        {(control, formApi) => {
          const photoType = formApi.watch('type');
          const isFinancing = photoType === 'FINANCING';
          const financialProductType = formApi.watch('financialProductType');
          // The preview result belongs to the draft it was computed from; only the fields of the
          // draft are watched, so the rest of the form does not re-render the card.
          const [vehicleSelectionMode, filter, autoAssign] = formApi.watch([
            'vehicleSelectionMode',
            'filter',
            'autoAssign',
          ]);
          const previewDraftKey = JSON.stringify({
            vehicleSelection: getPromoPhotoVehicleSelection({
              type: photoType,
              vehicleSelectionMode,
              filter,
            }),
            autoAssign,
          });
          const composition = formApi.watch('composition');
          const isSourcePhotoVisible = hasPhotoLayer(composition?.layers ?? []);

          return (
            <Card
              title={
                editData
                  ? i18n.t('page.salesSettings.labels.editPromoPhoto')
                  : i18n.t('page.salesSettings.labels.newPromoPhoto')
              }
            >
              <UnsuportedPlatformsNotification />
              <Show when={isFinancingEnabled}>
                {/* The type decides which BE fields exist, so it is fixed once the photo is created. */}
                <Hide when={isEditing}>
                  <FormField
                    type="radio"
                    control={control}
                    name="type"
                    label={i18n.t('general.labels.type')}
                    options={PHOTO_TYPE_OPTIONS}
                    data-testid="settings-advertising-promoPhotos-form-type"
                  />
                </Hide>
                <Show when={isEditing}>
                  <VStack spacing={1}>
                    <Text size="xSmall" color="secondary">
                      {i18n.t('general.labels.type')}
                    </Text>
                    <Text data-testid="settings-advertising-promoPhotos-form-typeValue">
                      {PHOTO_TYPE_OPTIONS.find((option) => option.value === photoType)?.label}
                    </Text>
                  </VStack>
                </Show>
                <Separator />
              </Show>
              <Grid columns={3}>
                <GridItem span={2}>
                  <FormField
                    type="text"
                    control={control}
                    label={i18n.t('entity.promoPhotos.title')}
                    name="title"
                  />
                </GridItem>
                <GridItem>
                  <FormField
                    type="integer"
                    control={control}
                    label={i18n.t('entity.promoPhotos.order')}
                    name="position"
                  />
                </GridItem>
              </Grid>
              <Separator />
              <Hide when={isFinancing}>
                <FormField
                  type="uploadImage"
                  control={control}
                  name="fileId"
                  uploadFileRequest={handleUpload}
                  onUploadError={notifyUnsupportedImageFormat}
                  helperText={`${i18n.t('entity.photo.labels.uploadOrDragAndDropImage')}. ${i18n.t(
                    'entity.photo.labels.maxUploadFileSize',
                    {
                      max: MAX_PROMO_PHOTO_SIZE_MB,
                    }
                  )}`}
                  uploadingText={i18n.t('general.labels.uploadingWithDots')}
                  uploadIcon="image/photo_camera"
                  linkValueFormatter={(url) => url.replace('resize', 'get')}
                  srcValueFormatter={(url) =>
                    `${url}${url.includes('resize') ? '&height=108' : ''}`
                  }
                  onPreviewClick={(url) => window.open(url.replace('resize', 'get'), '_blank')}
                />
              </Hide>
              <Show when={isFinancing}>
                <Grid columns={3}>
                  <GridItem span={2}>
                    <FormField
                      type="choice"
                      control={control}
                      name="financialProductType"
                      label={i18n.t('entity.promoPhotos.labels.financialProductType')}
                      options={FINANCIAL_PRODUCT_OPTIONS}
                      isNotClearable
                      isRequired
                      data-testid="settings-advertising-promoPhotos-form-financialProductType"
                    />
                  </GridItem>
                  <Show when={isSourcePhotoVisible}>
                    <GridItem>
                      <FormField
                        type="integer"
                        control={control}
                        name="sourcePhotoIndex"
                        label={i18n.t('entity.promoPhotos.labels.sourcePhotoIndex')}
                        helperText={i18n.t('entity.promoPhotos.labels.sourcePhotoIndexHelper')}
                        minStepperValue={MIN_SOURCE_PHOTO_INDEX}
                        isStepperVisible
                        isRequired
                        data-testid="settings-advertising-promoPhotos-form-sourcePhotoIndex"
                      />
                    </GridItem>
                  </Show>
                </Grid>
                <Separator />
                <CompositionGraphicSection
                  mode="financing"
                  financialProductType={financialProductType ?? undefined}
                  sourcePhotoIndex={formApi.watch('sourcePhotoIndex')}
                  composition={composition}
                  isDisabled={isNil(financialProductType)}
                  onApply={(result) => {
                    // The source photo field reappears with the photo slot; it starts at the first photo.
                    if (
                      hasPhotoLayer(result.scene.layers) &&
                      isNil(formApi.getValues('sourcePhotoIndex'))
                    ) {
                      formApi.setValue('sourcePhotoIndex', DEFAULT_SOURCE_PHOTO_INDEX);
                    }
                    formApi.setValue('composition', result.scene, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onRemove={() =>
                    formApi.setValue('composition', null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  data-testid="settings-advertising-promoPhotos-form-graphic"
                />
                <Show when={isNotNil(formApi.formState.errors.composition)}>
                  <Text size="xSmall" color="danger">
                    {formApi.formState.errors.composition?.message}
                  </Text>
                </Show>
              </Show>
              <Separator />
              {/* Vehicle selection rules only exist for REGULAR photos (a FINANCING one rejects them). */}
              <Show when={isVehicleSelectionEnabled && !isFinancing}>
                <PromoPhotoVehicleSelectionSection
                  control={control}
                  formApi={formApi}
                  data-testid="settings-advertising-promoPhotos-form-vehicleSelection"
                />
                <Separator />
              </Show>
              {/* The switch control has no tooltip of its own, so the whole row carries it. */}
              <Tooltip
                description={AUTO_ASSIGN_TEXTS[photoType].tooltip}
                childrenWrapperDisplay="block"
                data-testid="settings-advertising-promoPhotos-form-autoAssignTooltip"
              >
                <FormField
                  type="switch"
                  control={control}
                  name="autoAssign"
                  label={AUTO_ASSIGN_TEXTS[photoType].label}
                  helperText={AUTO_ASSIGN_TEXTS[photoType].helperText}
                  data-testid="settings-advertising-promoPhotos-form-autoAssign"
                />
              </Tooltip>
              <Show when={isVehicleSelectionEnabled && !isFinancing}>
                <Separator />
                <VehicleSelectionImpactPreview
                  promotionalPhotoId={editData?.id}
                  getDraft={async () => {
                    // The same rules as Save: an empty "by conditions" draft or a descending
                    // range shows its error instead of previewing a filter that matches every
                    // vehicle.
                    const isDraftValid = await formApi.trigger(['vehicleSelectionMode', 'filter']);
                    if (!isDraftValid) {
                      return null;
                    }
                    const values = formApi.getValues();

                    return {
                      vehicleSelection: getPromoPhotoVehicleSelection(values),
                      autoAssign: values.autoAssign,
                    };
                  }}
                  draftKey={previewDraftKey}
                  data-testid="settings-advertising-promoPhotos-form-vehicleSelectionPreview"
                />
              </Show>
              <Separator />
              <HStack spacing={2} justify="flex-end">
                <Link to={settingsRoutes.advertisingPromoPhotos}>
                  <Button variant="secondary" title={i18n.t('general.actions.cancel')} />
                </Link>
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  isLoading={createLoading || editLoading}
                  title={i18n.t('general.actions.save')}
                />
              </HStack>
            </Card>
          );
        }}
      </Form>
      <Show when={dialogType}>{(dialogType) => <Dialogs {...dialogType} />}</Show>
    </>
  );
}
